// Supported UI languages. Order is the picker order, copied from Insights:
// en → es → fr → de → it → pt-BR → pt-PT → pl → he.
// English is US English. Spanish is Spain Spanish (informal tú). Portuguese
// is split: Brazil (pt-BR) and Portugal (pt-PT).

export type LanguageCode =
  | 'en'
  | 'es'
  | 'fr'
  | 'de'
  | 'it'
  | 'pt-BR'
  | 'pt-PT'
  | 'pl'
  | 'he';

export type TextDirection = 'ltr' | 'rtl';

export interface LanguageMeta {
  code: LanguageCode;
  /** Short code shown on the closed picker button (e.g. EN, ES). */
  short: string;
  /** Native-language label in the open menu. */
  nativeLabel: string;
  /** Flag emoji (Unicode, no image). */
  flag: string;
  /** BCP-47 tag for Intl formatters and the document language. */
  locale: string;
}

export const SUPPORTED_LANGUAGES: ReadonlyArray<LanguageMeta> = [
  { code: 'en', short: 'EN', nativeLabel: 'English', flag: '🇺🇸', locale: 'en-US' },
  { code: 'es', short: 'ES', nativeLabel: 'Español', flag: '🇪🇸', locale: 'es-ES' },
  { code: 'fr', short: 'FR', nativeLabel: 'Français', flag: '🇫🇷', locale: 'fr-FR' },
  { code: 'de', short: 'DE', nativeLabel: 'Deutsch', flag: '🇩🇪', locale: 'de-DE' },
  { code: 'it', short: 'IT', nativeLabel: 'Italiano', flag: '🇮🇹', locale: 'it-IT' },
  {
    code: 'pt-BR',
    short: 'BR',
    nativeLabel: 'Português (Brasil)',
    flag: '🇧🇷',
    locale: 'pt-BR',
  },
  {
    code: 'pt-PT',
    short: 'PT',
    nativeLabel: 'Português (Portugal)',
    flag: '🇵🇹',
    locale: 'pt-PT',
  },
  { code: 'pl', short: 'PL', nativeLabel: 'Polski', flag: '🇵🇱', locale: 'pl-PL' },
  { code: 'he', short: 'HE', nativeLabel: 'עברית', flag: '🇮🇱', locale: 'he-IL' },
];

export const DEFAULT_LANGUAGE: LanguageCode = 'en';

/** The 8 catalogs a release must include once any translation file exists. */
export const TRANSLATION_CODES: ReadonlyArray<Exclude<LanguageCode, 'en'>> = [
  'es',
  'fr',
  'de',
  'it',
  'pt-BR',
  'pt-PT',
  'pl',
  'he',
];

export const RTL_LANGUAGES: ReadonlySet<LanguageCode> = new Set(['he']);

export type MessageCatalog = Readonly<Record<string, string>>;

/** Manual wizard choice. Only a pick is stored; a Genesys default is not. */
export const STORAGE_KEY = 'mediabriz.wizard.lang';

/**
 * A value this wizard saved. Exact supported codes only (case-insensitive).
 * Bare `pt` is not accepted: that migration belongs to Insights' old storage.
 */
export function normalizeStoredLanguage(value: string | null | undefined): LanguageCode | null {
  if (!value) return null;
  const trimmed = value.trim().toLowerCase();
  const match = SUPPORTED_LANGUAGES.find((language) => language.code.toLowerCase() === trimmed);
  return match?.code ?? null;
}

export function getLanguageMeta(code: LanguageCode): LanguageMeta {
  return SUPPORTED_LANGUAGES.find((language) => language.code === code) ?? SUPPORTED_LANGUAGES[0];
}

export function getDirection(code: LanguageCode): TextDirection {
  return RTL_LANGUAGES.has(code) ? 'rtl' : 'ltr';
}

/** Sets the document language and direction for the language the screen shows. */
export function applyDocumentLanguage(code: LanguageCode): void {
  const meta = getLanguageMeta(code);
  document.documentElement.lang = meta.locale;
  document.documentElement.dir = getDirection(code);
}
